import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import HubIcon from '@mui/icons-material/Hub';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  fetchRequestWorkflows,
  publishRequestWorkflow,
  saveRequestWorkflowDraft,
  simulateRequestWorkflow,
  validateRequestWorkflow,
} from '../../services/requestWorkflowsApi';

const NODE_WIDTH = 210;
const NODE_HEIGHT = 96;
const CANVAS_MIN_WIDTH = 1120;
const CANVAS_MIN_HEIGHT = 620;
const BRANCHES = ['default', 'true', 'false'];
const VALUELESS_OPERATORS = ['empty', 'not_empty'];
const LIST_OPERATORS = ['in', 'not_in'];

const asArray = (value) => (Array.isArray(value) ? value : []);
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const toKey = (value) => String(value || '').trim();

const nodeDefaults = {
  start: { label: 'Start' },
  approval: {
    label: 'Approval',
    required: true,
    approver: { resolver: 'requester_manager', employee_code: '' },
  },
  condition: {
    label: 'Condition',
    condition_logic: 'and',
    conditions: [{ field: 'amount', operator: 'greater_or_equal', value: '1000' }],
  },
  end: { label: 'End' },
};

const nodeStyles = {
  start: { bg: '#ECFDF5', border: '#10B981', color: '#065F46' },
  approval: { bg: '#EFF6FF', border: '#2563EB', color: '#1E3A8A' },
  condition: { bg: '#FFFBEB', border: '#F59E0B', color: '#92400E' },
  end: { bg: '#F8FAFC', border: '#64748B', color: '#334155' },
};

const branchLabel = (branch) => {
  if (branch === 'true') return 'True';
  if (branch === 'false') return 'False';
  return 'Next';
};

const optionValue = (option) => {
  if (isObject(option)) return String(option.value ?? option.key ?? option.id ?? option.label ?? '');
  return String(option ?? '');
};

const optionLabel = (option, field) => {
  const raw = isObject(option) ? String(option.label ?? option.name ?? optionValue(option)) : String(option ?? '');
  if (field?.type === 'boolean') {
    if (raw === '1' || raw.toLowerCase() === 'true') return 'Yes';
    if (raw === '0' || raw.toLowerCase() === 'false') return 'No';
  }
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const splitListValue = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);

const optionForValue = (options, value) => (
  options.find((option) => optionValue(option) === String(value || '')) || (value ? String(value) : null)
);

const normalizeConditionValue = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? '1' : '0';
  return value == null ? '' : String(value);
};

const normalizeNode = (node, index = 0) => {
  const type = ['start', 'approval', 'condition', 'end'].includes(node?.type) ? node.type : 'approval';
  const defaults = nodeDefaults[type];
  const data = isObject(node?.data) ? node.data : {};
  const position = isObject(node?.position) ? node.position : {};

  return {
    id: toKey(node?.id || `node_${index + 1}`),
    type,
    position: {
      x: Number.isFinite(Number(position.x)) ? Number(position.x) : 80 + index * 260,
      y: Number.isFinite(Number(position.y)) ? Number(position.y) : 180,
    },
    data: {
      ...defaults,
      ...data,
      label: toKey(data.label || defaults.label || type),
      approver: {
        ...(defaults.approver || {}),
        ...(isObject(data.approver) ? data.approver : {}),
      },
      conditions: asArray(data.conditions).map((condition) => ({
        field: condition?.field || '',
        operator: condition?.operator || 'equals',
        value: normalizeConditionValue(condition?.value ?? condition?.values),
      })),
      condition_logic: type === 'condition'
        ? (['or', 'any'].includes(String(data.condition_logic || data.logic || '').toLowerCase()) ? 'or' : 'and')
        : data.condition_logic,
    },
  };
};

const normalizeEdge = (edge, index = 0) => {
  const source = toKey(edge?.source);
  const target = toKey(edge?.target);
  const branch = BRANCHES.includes(edge?.branch) ? edge.branch : 'default';
  return {
    id: toKey(edge?.id || `${source}_${branch}_${target}_${index}`),
    source,
    target,
    branch,
    label: edge?.label || (branch === 'default' ? '' : branchLabel(branch)),
  };
};

const normalizeGraph = (graph) => {
  const nodes = asArray(graph?.nodes).map(normalizeNode).filter((node) => node.id);
  const ids = new Set(nodes.map((node) => node.id));
  const edges = asArray(graph?.edges)
    .map(normalizeEdge)
    .filter((edge) => edge.source && edge.target && ids.has(edge.source) && ids.has(edge.target));

  return {
    nodes,
    edges,
    settings: isObject(graph?.settings) ? graph.settings : {},
  };
};

const normalizeWorkflow = (workflow) => {
  const graph = normalizeGraph(workflow?.graph || workflow?.draft_version?.graph || {});
  return {
    ...workflow,
    display_name: workflow?.display_name || workflow?.request_type || '',
    description: workflow?.description || '',
    is_active: workflow?.is_active !== false,
    graph,
    validation: workflow?.validation || { valid: false, errors: [], warnings: [] },
    published_version: workflow?.published_version || null,
    draft_version: workflow?.draft_version || null,
  };
};

const makeNode = (type, existingNodes, anchor) => {
  const base = type === 'approval' ? 'approval' : type;
  let index = existingNodes.length + 1;
  let id = `${base}_${index}`;
  const used = new Set(existingNodes.map((node) => node.id));
  while (used.has(id)) {
    index += 1;
    id = `${base}_${index}`;
  }

  return normalizeNode({
    id,
    type,
    position: {
      x: Math.max(80, (anchor?.position?.x || 80) + 280),
      y: Math.max(80, anchor?.position?.y || 180),
    },
    data: { ...nodeDefaults[type], label: nodeDefaults[type]?.label || type },
  }, index);
};

const graphBounds = (graph) => {
  const maxX = Math.max(CANVAS_MIN_WIDTH, ...graph.nodes.map((node) => node.position.x + NODE_WIDTH + 120));
  const maxY = Math.max(CANVAS_MIN_HEIGHT, ...graph.nodes.map((node) => node.position.y + NODE_HEIGHT + 120));
  return { width: maxX, height: maxY };
};

const buildIssueMap = (validation) => {
  const map = new Map();
  [...asArray(validation?.errors), ...asArray(validation?.warnings)].forEach((issue) => {
    if (!issue?.node_id) return;
    const items = map.get(issue.node_id) || [];
    items.push(issue);
    map.set(issue.node_id, items);
  });
  return map;
};

const edgePath = (source, target) => {
  const sx = source.position.x + NODE_WIDTH;
  const sy = source.position.y + NODE_HEIGHT / 2;
  const tx = target.position.x;
  const ty = target.position.y + NODE_HEIGHT / 2;
  const mid = Math.max(60, Math.abs(tx - sx) / 2);
  return `M ${sx} ${sy} C ${sx + mid} ${sy}, ${tx - mid} ${ty}, ${tx} ${ty}`;
};

const sampleJson = `{
  "amount": 2500,
  "leave_type": "personal",
  "priority": "high",
  "pre_approval_billing_mode": "billed",
  "department_id": 1
}`;

function RequestWorkflowAdmin() {
  const [workflows, setWorkflows] = useState([]);
  const [conditionFields, setConditionFields] = useState([]);
  const [conditionOperators, setConditionOperators] = useState([]);
  const [approverResolvers, setApproverResolvers] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState('start');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [sampleData, setSampleData] = useState(sampleJson);
  const [simulation, setSimulation] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const dragRef = useRef(null);

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const payload = await fetchRequestWorkflows();
      const rows = asArray(payload?.data).map(normalizeWorkflow);
      setWorkflows(rows);
      setConditionFields(asArray(payload?.condition_fields));
      setConditionOperators(asArray(payload?.condition_operators));
      setApproverResolvers(asArray(payload?.approver_resolvers));
      setSelectedType((current) => current || rows[0]?.request_type || '');
      setSelectedNodeId('start');
    } catch (err) {
      console.error('Failed to load workflows', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load request workflows.');
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.request_type === selectedType) || null,
    [selectedType, workflows],
  );

  const graph = useMemo(
    () => selectedWorkflow?.graph || { nodes: [], edges: [], settings: {} },
    [selectedWorkflow],
  );
  const selectedNode = graph.nodes.find((node) => node.id === selectedNodeId) || graph.nodes[0] || null;
  const nodesById = useMemo(() => new Map(graph.nodes.map((node) => [node.id, node])), [graph.nodes]);
  const issueMap = useMemo(() => buildIssueMap(selectedWorkflow?.validation), [selectedWorkflow]);
  const bounds = useMemo(() => graphBounds(graph), [graph]);
  const validationErrors = asArray(selectedWorkflow?.validation?.errors);
  const validationWarnings = asArray(selectedWorkflow?.validation?.warnings);

  const updateSelectedWorkflow = useCallback((updater) => {
    setWorkflows((current) => current.map((workflow) => {
      if (workflow.request_type !== selectedType) return workflow;
      return updater(workflow);
    }));
  }, [selectedType]);

  const updateGraph = useCallback((updater) => {
    updateSelectedWorkflow((workflow) => ({
      ...workflow,
      graph: normalizeGraph(updater(workflow.graph)),
      validation: { ...workflow.validation, valid: false },
    }));
  }, [updateSelectedWorkflow]);

  const updateNode = useCallback((nodeId, updater) => {
    updateGraph((currentGraph) => ({
      ...currentGraph,
      nodes: currentGraph.nodes.map((node) => (node.id === nodeId ? normalizeNode(updater(node)) : node)),
    }));
  }, [updateGraph]);

  const setBranchTarget = useCallback((sourceId, branch, targetId) => {
    updateGraph((currentGraph) => {
      const edges = currentGraph.edges.filter((edge) => !(edge.source === sourceId && edge.branch === branch));
      if (targetId) {
        edges.push(normalizeEdge({
          source: sourceId,
          target: targetId,
          branch,
          label: branch === 'default' ? '' : branchLabel(branch),
        }));
      }
      return { ...currentGraph, edges };
    });
  }, [updateGraph]);

  const branchTarget = useCallback((sourceId, branch) => (
    graph.edges.find((edge) => edge.source === sourceId && edge.branch === branch)?.target || ''
  ), [graph.edges]);

  const addNode = useCallback((type) => {
    updateGraph((currentGraph) => {
      const anchor = currentGraph.nodes.find((node) => node.id === selectedNodeId)
        || currentGraph.nodes.find((node) => node.type === 'start')
        || currentGraph.nodes[0];
      const node = makeNode(type, currentGraph.nodes, anchor);
      const edges = [...currentGraph.edges];

      if (anchor && anchor.type !== 'end') {
        const branch = anchor.type === 'condition'
          ? (edges.some((edge) => edge.source === anchor.id && edge.branch === 'true') ? 'false' : 'true')
          : 'default';
        edges.push(normalizeEdge({
          source: anchor.id,
          target: node.id,
          branch,
          label: branch === 'default' ? '' : branchLabel(branch),
        }));
      }

      setSelectedNodeId(node.id);
      return { ...currentGraph, nodes: [...currentGraph.nodes, node], edges };
    });
  }, [selectedNodeId, updateGraph]);

  const deleteSelectedNode = useCallback(() => {
    if (!selectedNode || selectedNode.type === 'start') return;
    updateGraph((currentGraph) => ({
      ...currentGraph,
      nodes: currentGraph.nodes.filter((node) => node.id !== selectedNode.id),
      edges: currentGraph.edges.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id),
    }));
    setSelectedNodeId('start');
  }, [selectedNode, updateGraph]);

  const autoLayout = useCallback(() => {
    updateGraph((currentGraph) => {
      const sorted = [...currentGraph.nodes].sort((a, b) => {
        const typeRank = { start: 0, approval: 1, condition: 1, end: 2 };
        return (typeRank[a.type] || 1) - (typeRank[b.type] || 1) || a.id.localeCompare(b.id);
      });
      return {
        ...currentGraph,
        nodes: sorted.map((node, index) => ({
          ...node,
          position: {
            x: 80 + index * 260,
            y: 170 + (index % 2) * 120,
          },
        })),
      };
    });
  }, [updateGraph]);

  const onNodeMouseDown = useCallback((event, node) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    setSelectedNodeId(node.id);
    dragRef.current = {
      nodeId: node.id,
      startX: event.clientX,
      startY: event.clientY,
      origin: { ...node.position },
    };
  }, []);

  useEffect(() => {
    const onMove = (event) => {
      if (!dragRef.current) return;
      const { nodeId, startX, startY, origin } = dragRef.current;
      const nextX = Math.max(20, origin.x + event.clientX - startX);
      const nextY = Math.max(20, origin.y + event.clientY - startY);
      updateNode(nodeId, (node) => ({ ...node, position: { x: nextX, y: nextY } }));
    };
    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [updateNode]);

  const runValidation = useCallback(async ({ announce = false } = {}) => {
    if (!selectedWorkflow) return null;
    try {
      setValidating(true);
      setError('');
      if (announce) setSuccess('');
      const payload = await validateRequestWorkflow(selectedWorkflow.request_type, { graph });
      updateSelectedWorkflow((workflow) => ({
        ...workflow,
        validation: payload?.validation || workflow.validation,
      }));
      const validation = payload?.validation || null;
      // The Validate button used to silently update the badge; users had no
      // confirmation the action ran. The publish-time call passes
      // announce=false so it doesn't compete with the publish toast.
      if (announce) {
        if (validation?.valid) {
          const issueCount = Array.isArray(validation?.issues) ? validation.issues.length : 0;
          setSuccess(issueCount > 0
            ? `Workflow is valid. ${issueCount} non-blocking note${issueCount === 1 ? '' : 's'}.`
            : 'Workflow is valid.');
        } else {
          const issueCount = Array.isArray(validation?.issues) ? validation.issues.length : 0;
          setError(issueCount > 0
            ? `Workflow is invalid. Fix the ${issueCount} issue${issueCount === 1 ? '' : 's'} highlighted in the graph.`
            : 'Workflow is invalid. Fix the highlighted issues.');
        }
      }
      return validation;
    } catch (err) {
      console.error('Workflow validation failed', err);
      setError(err?.response?.data?.message || err?.message || 'Workflow validation failed.');
      return null;
    } finally {
      setValidating(false);
    }
  }, [graph, selectedWorkflow, updateSelectedWorkflow]);

  const saveDraft = useCallback(async () => {
    if (!selectedWorkflow) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = await saveRequestWorkflowDraft(selectedWorkflow.request_type, {
        display_name: selectedWorkflow.display_name,
        description: selectedWorkflow.description,
        is_active: selectedWorkflow.is_active,
        graph,
      });
      const next = normalizeWorkflow(payload?.data || selectedWorkflow);
      setWorkflows((current) => current.map((workflow) => (
        workflow.request_type === next.request_type ? next : workflow
      )));
      setSuccess('Draft saved.');
    } catch (err) {
      console.error('Failed to save draft', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  }, [graph, selectedWorkflow]);

  const publishWorkflow = useCallback(async () => {
    if (!selectedWorkflow) return;
    const validation = await runValidation();
    if (!validation?.valid) {
      setError('Fix validation errors before publishing.');
      return;
    }

    try {
      setPublishing(true);
      setError('');
      setSuccess('');
      const payload = await publishRequestWorkflow(selectedWorkflow.request_type);
      const next = normalizeWorkflow(payload?.data || selectedWorkflow);
      setWorkflows((current) => current.map((workflow) => (
        workflow.request_type === next.request_type ? next : workflow
      )));
      setSuccess('Workflow published. Newly created requests will use this version.');
    } catch (err) {
      console.error('Failed to publish workflow', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to publish workflow.');
    } finally {
      setPublishing(false);
    }
  }, [runValidation, selectedWorkflow]);

  const runSimulation = useCallback(async () => {
    if (!selectedWorkflow) return;
    try {
      setSimulating(true);
      setError('');
      const parsed = JSON.parse(sampleData || '{}');
      const payload = await simulateRequestWorkflow(selectedWorkflow.request_type, {
        graph,
        sample_data: parsed,
      });
      setSimulation(payload?.simulation || null);
    } catch (err) {
      console.error('Workflow simulation failed', err);
      setError(err instanceof SyntaxError
        ? 'Sample request data must be valid JSON.'
        : err?.response?.data?.message || err?.message || 'Workflow simulation failed.');
    } finally {
      setSimulating(false);
    }
  }, [graph, sampleData, selectedWorkflow]);

  const updateWorkflowField = useCallback((field, value) => {
    updateSelectedWorkflow((workflow) => ({ ...workflow, [field]: value }));
  }, [updateSelectedWorkflow]);

  const selectedNodeIssues = selectedNode ? asArray(issueMap.get(selectedNode.id)) : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', p: { xs: 2, lg: 3 } }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HubIcon color="primary" />
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Request Workflow Engine
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Draft visual approval paths, validate branches, simulate routing, then publish a frozen version for new requests.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {loading && workflows.length > 0 ? (
              <CircularProgress size={18} thickness={5} sx={{ mr: 0.5 }} />
            ) : null}
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadWorkflows}
              disabled={loading}
            >
              {loading && workflows.length > 0 ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button
              startIcon={<CheckCircleIcon />}
              onClick={() => runValidation({ announce: true })}
              disabled={!selectedWorkflow || validating}
            >
              {validating ? 'Validating…' : 'Validate'}
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveDraft} disabled={!selectedWorkflow || saving}>
              Save Draft
            </Button>
            <Button
              color="success"
              variant="contained"
              startIcon={<PublishIcon />}
              onClick={() => setPublishConfirmOpen(true)}
              disabled={!selectedWorkflow || publishing || validationErrors.length > 0}
            >
              Publish
            </Button>
          </Stack>
        </Stack>

        <Dialog
          open={publishConfirmOpen}
          onClose={() => (publishing ? null : setPublishConfirmOpen(false))}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Publish workflow?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Publishing will replace the runtime version of the
              <strong> {selectedWorkflow?.display_name || selectedWorkflow?.request_type} </strong>
              workflow. <strong>Newly created requests</strong> will use this version
              immediately. Requests already in flight keep the version they started on.
            </DialogContentText>
            {selectedWorkflow?.published_version?.id ? (
              <DialogContentText sx={{ mt: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                Currently published: v{selectedWorkflow.published_version.version_number || selectedWorkflow.published_version.id}
              </DialogContentText>
            ) : (
              <DialogContentText sx={{ mt: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}>
                No version is published yet — this will be the first.
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPublishConfirmOpen(false)} disabled={publishing}>
              Cancel
            </Button>
            <Button
              color="success"
              variant="contained"
              startIcon={<PublishIcon />}
              onClick={async () => {
                setPublishConfirmOpen(false);
                await publishWorkflow();
              }}
              disabled={publishing}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

        {loading && workflows.length === 0 ? (
          // First-load only — once we have workflows, refreshes keep the
          // layout in place and show the spinner inside the toolbar instead
          // of wiping the whole panel.
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Paper>
        ) : (
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems="stretch"
            sx={{ position: 'relative', opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
          >
            <WorkflowList
              workflows={workflows}
              selectedType={selectedType}
              onSelect={(requestType) => {
                setSelectedType(requestType);
                setSelectedNodeId('start');
                setSimulation(null);
              }}
            />

            <Paper sx={{ flex: 1, minWidth: 0, overflow: 'hidden', borderRadius: 2 }}>
              <Stack spacing={1.5} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {selectedWorkflow?.display_name || 'Workflow'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedWorkflow?.published_version
                        ? `Published v${selectedWorkflow.published_version.version_number || '-'}`
                        : 'No published version yet'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addNode('approval')}>Approval</Button>
                    <Button size="small" startIcon={<CallSplitIcon />} onClick={() => addNode('condition')}>Condition</Button>
                    <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => addNode('end')}>End</Button>
                    <Button size="small" startIcon={<AccountTreeIcon />} onClick={autoLayout}>Auto Layout</Button>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={validationErrors.length ? 'error' : 'success'}
                    icon={validationErrors.length ? <WarningAmberIcon /> : <CheckCircleIcon />}
                    label={validationErrors.length ? `${validationErrors.length} validation error${validationErrors.length === 1 ? '' : 's'}` : 'Valid graph'}
                  />
                  {!!validationWarnings.length && (
                    <Chip size="small" color="warning" icon={<WarningAmberIcon />} label={`${validationWarnings.length} warning${validationWarnings.length === 1 ? '' : 's'}`} />
                  )}
                  <Chip size="small" label={`${graph.nodes.length} nodes`} />
                  <Chip size="small" label={`${graph.edges.length} edges`} />
                </Stack>
              </Stack>

              <Box
                sx={{
                  position: 'relative',
                  overflow: 'auto',
                  height: { xs: 520, lg: 'calc(100vh - 250px)' },
                  minHeight: 520,
                  bgcolor: '#FFFFFF',
                  backgroundImage: 'radial-gradient(#CBD5E1 1px, transparent 1px)',
                  backgroundSize: '22px 22px',
                }}
                onMouseDown={() => setSelectedNodeId('')}
              >
                <Box sx={{ position: 'relative', width: bounds.width, height: bounds.height }}>
                  <svg width={bounds.width} height={bounds.height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    <defs>
                      <marker id="workflow-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748B" />
                      </marker>
                    </defs>
                    {graph.edges.map((edge) => {
                      const source = nodesById.get(edge.source);
                      const target = nodesById.get(edge.target);
                      if (!source || !target) return null;
                      const d = edgePath(source, target);
                      const labelX = (source.position.x + target.position.x + NODE_WIDTH) / 2;
                      const labelY = (source.position.y + target.position.y + NODE_HEIGHT) / 2 - 10;
                      return (
                        <g key={edge.id}>
                          <path d={d} fill="none" stroke="#64748B" strokeWidth="2.2" markerEnd="url(#workflow-arrow)" />
                          {edge.branch !== 'default' && (
                            <text x={labelX} y={labelY} fontSize="12" fontWeight="700" fill="#334155">
                              {branchLabel(edge.branch)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>

                  {graph.nodes.map((node) => {
                    const style = nodeStyles[node.type] || nodeStyles.approval;
                    const issues = asArray(issueMap.get(node.id));
                    const hasError = issues.some((issue) => issue.severity !== 'warning');
                    const isSelected = selectedNodeId === node.id;
                    return (
                      <Box
                        key={node.id}
                        onMouseDown={(event) => onNodeMouseDown(event, node)}
                        sx={{
                          position: 'absolute',
                          left: node.position.x,
                          top: node.position.y,
                          width: NODE_WIDTH,
                          minHeight: NODE_HEIGHT,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: style.bg,
                          border: '2px solid',
                          borderColor: hasError ? '#DC2626' : isSelected ? '#111827' : style.border,
                          boxShadow: isSelected ? '0 14px 34px rgba(15, 23, 42, 0.18)' : '0 8px 20px rgba(15, 23, 42, 0.08)',
                          cursor: 'grab',
                          userSelect: 'none',
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Chip size="small" label={node.type} sx={{ bgcolor: '#FFFFFF', color: style.color, fontWeight: 700 }} />
                            {!!issues.length && (
                              <Tooltip title={issues.map((issue) => issue.message).join(' | ')}>
                                <WarningAmberIcon fontSize="small" color={hasError ? 'error' : 'warning'} />
                              </Tooltip>
                            )}
                          </Stack>
                          <Typography variant="subtitle2" sx={{ color: style.color, fontWeight: 800, lineHeight: 1.2 }}>
                            {node.data.label}
                          </Typography>
                          {node.type === 'approval' && (
                            <Typography variant="caption" color="text.secondary">
                              {approverResolvers.find((item) => item.key === node.data?.approver?.resolver)?.label || 'No approver'}
                            </Typography>
                          )}
                          {node.type === 'condition' && (
                            <Typography variant="caption" color="text.secondary">
                              {(node.data.condition_logic || 'and').toUpperCase()} - {asArray(node.data.conditions).length} condition{asArray(node.data.conditions).length === 1 ? '' : 's'}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Paper>

            <InspectorPanel
              workflow={selectedWorkflow}
              node={selectedNode}
              graph={graph}
              conditionFields={conditionFields}
              conditionOperators={conditionOperators}
              approverResolvers={approverResolvers}
              nodeIssues={selectedNodeIssues}
              simulation={simulation}
              sampleData={sampleData}
              simulating={simulating}
              onWorkflowField={updateWorkflowField}
              onNodeChange={updateNode}
              onBranchTarget={setBranchTarget}
              branchTarget={branchTarget}
              onDeleteNode={deleteSelectedNode}
              onSampleDataChange={setSampleData}
              onRunSimulation={runSimulation}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function WorkflowList({ workflows, selectedType, onSelect }) {
  return (
    <Paper sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Request Types</Typography>
        <Typography variant="caption" color="text.secondary">Drafts are separate from published runtime versions.</Typography>
      </Box>
      <Stack sx={{ p: 1, maxHeight: { xs: 260, lg: 'calc(100vh - 220px)' }, overflow: 'auto' }} spacing={0.75}>
        {workflows.map((workflow) => {
          const selected = workflow.request_type === selectedType;
          const errors = asArray(workflow.validation?.errors).length;
          return (
            <Button
              key={workflow.request_type}
              onClick={() => onSelect(workflow.request_type)}
              variant={selected ? 'contained' : 'text'}
              color={errors ? 'error' : 'primary'}
              sx={{
                justifyContent: 'space-between',
                textTransform: 'none',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                  {workflow.display_name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.78 }} noWrap>
                  {workflow.request_type}
                </Typography>
              </Box>
              <Chip
                size="small"
                color={workflow.published_version ? 'success' : 'default'}
                label={workflow.published_version ? `v${workflow.published_version.version_number || '-'}` : 'draft'}
              />
            </Button>
          );
        })}
      </Stack>
    </Paper>
  );
}

function InspectorPanel({
  workflow,
  node,
  graph,
  conditionFields,
  conditionOperators,
  approverResolvers,
  nodeIssues,
  simulation,
  sampleData,
  simulating,
  onWorkflowField,
  onNodeChange,
  onBranchTarget,
  branchTarget,
  onDeleteNode,
  onSampleDataChange,
  onRunSimulation,
}) {
  const otherNodes = asArray(graph.nodes).filter((item) => item.id !== node?.id);

  const updateNodeData = (patch) => {
    if (!node) return;
    onNodeChange(node.id, (current) => ({
      ...current,
      data: {
        ...current.data,
        ...patch,
      },
    }));
  };

  const updateApprover = (patch) => {
    if (!node) return;
    onNodeChange(node.id, (current) => ({
      ...current,
      data: {
        ...current.data,
        approver: {
          ...(current.data?.approver || {}),
          ...patch,
        },
      },
    }));
  };

  const updateCondition = (index, patch) => {
    updateNodeData({
      conditions: asArray(node?.data?.conditions).map((condition, currentIndex) => (
        currentIndex === index ? { ...condition, ...patch } : condition
      )),
    });
  };

  const addCondition = () => {
    updateNodeData({
      conditions: [
        ...asArray(node?.data?.conditions),
        { field: conditionFields[0]?.key || '', operator: 'equals', value: '' },
      ],
    });
  };

  const removeCondition = (index) => {
    updateNodeData({
      conditions: asArray(node?.data?.conditions).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  return (
    <Paper sx={{ width: { xs: '100%', lg: 390 }, flexShrink: 0, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Inspector</Typography>
        <Typography variant="caption" color="text.secondary">Edit graph settings, selected node, and simulation data.</Typography>
      </Box>

      <Stack spacing={2} sx={{ p: 2, maxHeight: { lg: 'calc(100vh - 220px)' }, overflow: 'auto' }}>
        <Stack spacing={1.5}>
          <Typography variant="overline" color="text.secondary">Workflow</Typography>
          <TextField
            size="small"
            label="Display name"
            value={workflow?.display_name || ''}
            onChange={(event) => onWorkflowField('display_name', event.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Description"
            value={workflow?.description || ''}
            onChange={(event) => onWorkflowField('description', event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={<Switch checked={workflow?.is_active !== false} onChange={(event) => onWorkflowField('is_active', event.target.checked)} />}
            label="Available for new request publishing"
          />
        </Stack>

        <Divider />

        {!node ? (
          <Alert severity="info">Select a node on the canvas to edit its details.</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="overline" color="text.secondary">Selected Node</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{node.id}</Typography>
              </Box>
              <Tooltip title={node.type === 'start' ? 'Start node cannot be deleted' : 'Delete node'}>
                <span>
                  <IconButton color="error" onClick={onDeleteNode} disabled={node.type === 'start'}>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {!!nodeIssues.length && (
              <Alert severity={nodeIssues.some((issue) => issue.severity !== 'warning') ? 'error' : 'warning'}>
                <Stack spacing={0.5}>
                  {nodeIssues.map((issue, index) => (
                    <Typography key={`${issue.code}-${index}`} variant="caption">{issue.message}</Typography>
                  ))}
                </Stack>
              </Alert>
            )}

            <TextField
              size="small"
              label="Node label"
              value={node.data?.label || ''}
              onChange={(event) => updateNodeData({ label: event.target.value })}
              fullWidth
            />

            {node.type === 'approval' && (
              <Stack spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Approver rule</InputLabel>
                  <Select
                    label="Approver rule"
                    value={node.data?.approver?.resolver || ''}
                    onChange={(event) => updateApprover({ resolver: event.target.value })}
                  >
                    {approverResolvers.map((resolver) => (
                      <MenuItem key={resolver.key} value={resolver.key}>
                        {resolver.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {node.data?.approver?.resolver === 'specific_employee' && (
                  <TextField
                    size="small"
                    label="Employee code"
                    value={node.data?.approver?.employee_code || ''}
                    onChange={(event) => updateApprover({ employee_code: event.target.value })}
                    fullWidth
                  />
                )}
                <FormControlLabel
                  control={<Switch checked={node.data?.required !== false} onChange={(event) => updateNodeData({ required: event.target.checked })} />}
                  label="Required approval"
                />
              </Stack>
            )}

            {node.type === 'condition' && (
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="text.secondary">Conditions</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>Add</Button>
                </Stack>
                <FormControl size="small" fullWidth>
                  <InputLabel>Match conditions</InputLabel>
                  <Select
                    label="Match conditions"
                    value={node.data?.condition_logic || 'and'}
                    onChange={(event) => updateNodeData({ condition_logic: event.target.value })}
                  >
                    <MenuItem value="and">All conditions (AND)</MenuItem>
                    <MenuItem value="or">Any condition (OR)</MenuItem>
                  </Select>
                </FormControl>
                {asArray(node.data?.conditions).map((condition, index) => {
                  const valueless = VALUELESS_OPERATORS.includes(condition.operator);
                  const listOperator = LIST_OPERATORS.includes(condition.operator);
                  const selectedField = conditionFields.find((field) => field.key === condition.field);
                  const options = asArray(selectedField?.options);
                  const hasOptions = options.length > 0;
                  return (
                    <Paper key={index} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                      <Stack spacing={1}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Field</InputLabel>
                          <Select
                            label="Field"
                            value={condition.field || ''}
                            onChange={(event) => updateCondition(index, { field: event.target.value, value: '' })}
                          >
                            {conditionFields.map((field) => (
                              <MenuItem key={field.key} value={field.key}>{field.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Operator</InputLabel>
                          <Select
                            label="Operator"
                            value={condition.operator || 'equals'}
                            onChange={(event) => updateCondition(index, { operator: event.target.value })}
                          >
                            {conditionOperators.map((operator) => (
                              <MenuItem key={operator.key} value={operator.key}>{operator.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {!valueless && hasOptions && (
                          <Autocomplete
                            freeSolo
                            multiple={listOperator}
                            options={options}
                            value={listOperator
                              ? splitListValue(condition.value).map((value) => optionForValue(options, value))
                              : optionForValue(options, condition.value)}
                            getOptionLabel={(option) => optionLabel(option, selectedField)}
                            isOptionEqualToValue={(option, value) => optionValue(option) === optionValue(value)}
                            onChange={(_, value) => {
                              if (Array.isArray(value)) {
                                updateCondition(index, { value: value.map(optionValue).filter(Boolean).join(', ') });
                                return;
                              }
                              updateCondition(index, { value: optionValue(value) });
                            }}
                            onInputChange={(_, value, reason) => {
                              if (listOperator) return;
                              if (reason === 'input' || reason === 'clear') {
                                updateCondition(index, { value });
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label={listOperator ? 'Values' : 'Value'}
                                helperText="Select an existing value or type a custom value."
                              />
                            )}
                          />
                        )}
                        {!valueless && !hasOptions && (
                          <TextField
                            size="small"
                            label={listOperator ? 'Values (comma separated)' : 'Value'}
                            value={condition.value || ''}
                            onChange={(event) => updateCondition(index, { value: event.target.value })}
                            fullWidth
                          />
                        )}
                        <Button color="error" size="small" onClick={() => removeCondition(index)} startIcon={<DeleteIcon />}>
                          Remove condition
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {node.type !== 'end' && (
              <Stack spacing={1.25}>
                <Typography variant="overline" color="text.secondary">Branch Targets</Typography>
                {(node.type === 'condition' ? ['true', 'false', 'default'] : ['default']).map((branch) => (
                  <FormControl key={branch} size="small" fullWidth>
                    <InputLabel>{branchLabel(branch)} target</InputLabel>
                    <Select
                      label={`${branchLabel(branch)} target`}
                      value={branchTarget(node.id, branch)}
                      onChange={(event) => onBranchTarget(node.id, branch, event.target.value)}
                    >
                      <MenuItem value="">No target</MenuItem>
                      {otherNodes.map((target) => (
                        <MenuItem key={target.id} value={target.id}>
                          {target.data?.label || target.id} ({target.type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        <Divider />

        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="overline" color="text.secondary">Simulate</Typography>
              <Typography variant="body2" color="text.secondary">Use sample request data to preview the approval path.</Typography>
            </Box>
            <Button startIcon={<PlayArrowIcon />} onClick={onRunSimulation} disabled={simulating}>
              Run
            </Button>
          </Stack>
          <TextField
            value={sampleData}
            onChange={(event) => onSampleDataChange(event.target.value)}
            multiline
            minRows={8}
            size="small"
            fullWidth
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
          {simulation && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color={simulation.errors?.length ? 'error' : 'success'} label={simulation.status || 'unknown'} />
                  <Typography variant="caption" color="text.secondary">{asArray(simulation.path).length} visited nodes</Typography>
                </Stack>
                {asArray(simulation.errors).map((item, index) => (
                  <Alert key={index} severity="error">{item}</Alert>
                ))}
                <Stack spacing={0.75}>
                  {asArray(simulation.path).map((step, index) => (
                    <Box key={`${step.node_id}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip size="small" label={index + 1} />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{step.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{step.node_type}</Typography>
                      {step.condition_logic && <Chip size="small" label={step.condition_logic.toUpperCase()} />}
                      {step.decision && <Chip size="small" label={branchLabel(step.decision)} />}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default RequestWorkflowAdmin;
